import { sessionWritable } from '@macfja/svelte-persistent-store'
import { get } from 'svelte/store'
import { graphql, type Login$result, type PermissionLevel$options } from '$houdini'
import type { Merge } from 'type-fest'
import type { LicenseLimit, LicenseToggle } from 'utility/login-helper'

const session = sessionWritable<ClientSession | Record<string, never>>('user', {})

export default session

/** Runs `get` from `svelte/store` on the session store. You should only use this in the case where subscribing doesn't make sense. */
export function getSession(): ClientSession | Record<string, never> {
	return get(session)
}

export function isClientSession(session: ClientSession | Record<string, never>): session is ClientSession {
	return 'authToken' in session
}

export type ClientSession = Login$result['createSession']['userAccount'] & Merge<
	Omit<Login$result['createSession'], 'token' | 'userAccount' | 'companyInfo' | 'storeId' | 'permissions' | ' $fragments'>,
	{
		authToken: NonNullable<Login$result['createSession']['token']>
		authorizedStores: NonNullable<Login$result['createSession']['userAccount']['authorizedStores']>
		userAccountId: NonNullable<Login$result['createSession']['userAccount']['id']>
		currentStore: NonNullable<Login$result['createSession']['storeId']>
		/** A map of permissions to booleans, indicating whether the user has the permission at the logged-in store */
		sessionPermissions: NonNullable<Login$result['createSession']['permissions']>
		/** A map of permissions to permission level */
		userPermissions: Record<string, PermissionLevel$options>
		companyCode: NonNullable<Login$result['createSession']['companyInfo']['id']>
		licenseInfo: SessionLicenseInfo
	}
>

export type SessionLicenseInfo = {
	limits: Record<string, LicenseLimit | undefined>
	toggles: Record<string, LicenseToggle | undefined>
}

export async function refreshSessionLicenseInfo() {
	const { data } = await licenseQuery.fetch()
	if (!data) {
		throw new Error('Error fetching license info')
	}

	session.update((session): ClientSession | Record<string, never> => {
		if (!('authToken' in session)) {
			return session
		}

		session.licenseInfo.limits = data?.licenseInfo.limits.reduce((acc: Record<string, LicenseLimit>, limit) => {
			acc[limit.codeName] = limit
			return acc
		}, {})
		session.licenseInfo.toggles = data?.licenseInfo.toggles.reduce((acc: Record<string, LicenseToggle>, toggle) => {
			acc[toggle.codeName] = toggle
			return acc
		}, {})

		return session
	})
}

const licenseQuery = graphql(`
	query GetLicenseInfo {
		licenseInfo {
			limits {
				id
				codeName
				displayName
				description
				limit
				count
				belowLimit
			}
			toggles {
				id
				codeName
				displayName
				description
				permissionCode
				active
			}
		}
	}
`)
